/* The risk step is ours to derive, not a Kanopy field - tree.risk_score is
 * Kanopy's own composite, shown as sent and too sparse to derive anything
 * from (07-domain-mapping-spec.md section 3). Its steps are Kanopy's bands,
 * named as Kanopy names them (02 -> The risk step is Kanopy's bands, scored against our
 * threshold): a tree at or beyond its class's threshold is
 * Acceptable, and below it the lines fall where Kanopy's zones put them for
 * that class, scaled to the threshold in force. This is the single place that
 * math happens - every screen that shows a risk step calls this rather than
 * re-deriving it (CLAUDE.md rule 3).
 */
import type { ClearanceThreshold, RiskStep } from "../api/types";

/* Kanopy's red and orange edges for a class, as fractions of its green edge
 * (ClearanceThreshold). */
export type BandShape = Pick<ClearanceThreshold, "critical_fraction" | "high_fraction">;

/** Where each step below the threshold ends, in metres: a distance under
 * Critical is Critical, under High is High, under Medium - the threshold
 * itself - is Medium, and anything else is Acceptable. S10 shows these. */
export type BandLines = Record<Exclude<RiskStep, "Acceptable">, number>;

export function bandLines(thresholdM: number, shape: BandShape): BandLines {
  /* A threshold of zero, NaN or a negative makes every line zero or NaN, every
   * comparison against them false, and the step Acceptable - the most
   * reassuring answer there is, returned precisely when nothing is known.
   * That direction is the dangerous one: it reports a tree in contact with a
   * conductor as safe. So refuse instead (CLAUDE.md, Fail closed). A shape
   * whose lines are out of order is refused for the same reason.
   *
   * Not Critical either. Inventing the severe answer is still inventing one,
   * and a fabricated risk step is indistinguishable from a measured one once
   * it is on screen - the same reason /api/me marks role unresolved rather
   * than defaulting it. Callers hold a real threshold or they hold nothing.
   */
  if (!Number.isFinite(thresholdM) || thresholdM <= 0) {
    throw new Error(`bandLines: threshold must be a positive number, got ${thresholdM}`);
  }
  const { critical_fraction: critical, high_fraction: high } = shape;
  if (!(critical > 0 && critical < high && high < 1)) {
    throw new Error(`bandLines: band fractions must rise between 0 and 1, got ${critical} and ${high}`);
  }
  return { Critical: critical * thresholdM, High: high * thresholdM, Medium: thresholdM };
}

export function riskStepForClearance(measuredM: number, thresholdM: number, shape: BandShape): RiskStep {
  /* A missing distance has no step, and the screens say Unmeasured for it -
   * riskStepForSpan returns null before calling this.
   * Reaching here without one gives a wrong answer either way: NaN or
   * undefined fails every comparison and reads Acceptable, and null compares
   * as 0 and reads Critical. The type rules out both, but a value parsed from
   * JSON carries no type, so refuse rather than answer. */
  if (!Number.isFinite(measuredM)) {
    throw new Error(`riskStepForClearance: measurement must be a finite number, got ${measuredM}`);
  }
  const lines = bandLines(thresholdM, shape);
  if (measuredM < lines.Critical) return "Critical";
  if (measuredM < lines.High) return "High";
  if (measuredM < lines.Medium) return "Medium";
  return "Acceptable";
}

/* Least to most severe. The step is its own label: every screen shows the
 * step's name as Kanopy writes it, never a code standing in for it. */
export const RISK_STEPS: readonly RiskStep[] = ["Acceptable", "Medium", "High", "Critical"];

/* Kanopy's own band as a step, for the screens that show Kanopy's band where
 * the risk step goes - the Trees list, Tree detail and D04's tree list (02 ->
 * Kanopy's band is shown under Kanopy's name). Unmeasured is no step, which
 * RiskBadge draws as Unmeasured. */
export function bandStep(band: string): RiskStep | null {
  return RISK_STEPS.includes(band as RiskStep) ? (band as RiskStep) : null;
}

export interface StepChange {
  before: RiskStep;
  after: RiskStep;
}

/** Each tree's risk step at the threshold in force and at a proposed one, the
 * read S10 shows before a save (18 section 2.5). One pass, so the count of
 * trees that change step and the distribution either side of the save cannot
 * disagree. A class with no draft keeps its threshold, so its trees read the
 * same either side. A tree with no measured distance, or whose class has no
 * threshold, has no step to change and is left out. */
export function stepChanges(
  trees: readonly { voltage_class: string | null; min_absolute_distance_m: number | null }[],
  thresholds: readonly ClearanceThreshold[],
  proposedM: Readonly<Record<string, number>>,
): StepChange[] {
  const changes: StepChange[] = [];
  for (const tree of trees) {
    const t = thresholds.find((x) => x.voltage_class === tree.voltage_class);
    if (!t || tree.min_absolute_distance_m === null) continue;
    changes.push({
      before: riskStepForClearance(tree.min_absolute_distance_m, t.in_use_m, t),
      after: riskStepForClearance(tree.min_absolute_distance_m, proposedM[t.voltage_class] ?? t.in_use_m, t),
    });
  }
  return changes;
}

/** Whether a change moves a tree toward Critical, which is an increase in risk.
 * 18 section 2.5 shows increases and decreases apart, because a net count
 * hides an override that moves most trees the wrong way. */
export function isIncrease(change: StepChange): boolean {
  return RISK_STEPS.indexOf(change.after) > RISK_STEPS.indexOf(change.before);
}

/** Nearest the conductor first, with an unmeasured distance after every
 * measured one rather than sorting as zero (15 sections 3.2 and 3.3). Two
 * unmeasured compare equal, so a caller's own tie-break decides. */
export function compareClearance(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

/** How many of each step a list holds, in RISK_STEPS order with a zero where
 * there are none, so a distribution always carries an entry for every step. */
export function countSteps(steps: readonly RiskStep[]): Record<RiskStep, number> {
  const counts = Object.fromEntries(RISK_STEPS.map((step) => [step, 0])) as Record<RiskStep, number>;
  for (const step of steps) counts[step]++;
  return counts;
}

/** Distributions added step by step - a system-wide distribution is the sum
 * of its projects', never a second count over the same spans. */
export function sumStepCounts(all: readonly Record<RiskStep, number>[]): Record<RiskStep, number> {
  const total = countSteps([]);
  for (const counts of all) for (const step of RISK_STEPS) total[step] += counts[step];
  return total;
}

/** Spans below their threshold: every step but Acceptable, which starts at
 * the threshold. */
export function belowClearance(counts: Record<RiskStep, number>): number {
  return counts.Medium + counts.High + counts.Critical;
}

/** The measured spans a distribution holds - an unmeasured span has no step,
 * so it is never in one. Every share is over this, never over a count that
 * includes unmeasured spans: those would read as clear and pull every share
 * toward safe (02 -> The risk step is Kanopy's bands, scored against our threshold). */
export function measuredCount(counts: Record<RiskStep, number>): number {
  return RISK_STEPS.reduce((sum, s) => sum + counts[s], 0);
}

/** One step's share of a distribution, 0 rather than NaN when it is empty. */
export function stepShare(counts: Record<RiskStep, number>, step: RiskStep): number {
  const total = measuredCount(counts);
  return total === 0 ? 0 : counts[step] / total;
}

/** belowClearance as a share of the measured spans, 0 rather than NaN when
 * there are none. */
export function belowClearanceShare(counts: Record<RiskStep, number>): number {
  const total = measuredCount(counts);
  return total === 0 ? 0 : belowClearance(counts) / total;
}
